import { useEffect, useRef, useState } from "react";
import { styles } from "../styles";
import { createServiceTicket } from "../models/models";
import { displayName } from "../utils/capabilities";
import TicketSignOff from "./TicketSignOff";
import TicketActions from "./TicketActions";

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Per-job service tickets (work orders) — Phase 4.2b. Lists this job's
 * tickets and lets an assigned tech (or manager/admin) file a new one.
 * A ticket can carry multiple employees (each with their own hours + date),
 * so the customer signs ONE ticket. Materials carry the tech-entered cost
 * (internal only — hidden from the customer on the signed copy, Phase 4.2d).
 */
export default function JobServiceTickets({
  job,
  tickets,
  equipmentList,
  actions,
  user,
  profile = {},
  canFile,
  errorMessage,
  customer,
  location,
  customerName,
  locationName,
  jobsRaw = [],
  canManage,
  role,
  capabilities,
  logoUrl = "",
  orgName = "",
  orgAddress = {},
  jobPOs = [],
  orgUsers = [],
}) {
  // Default the first labor row to the signed-in user's DISPLAY name
  // (falls back to email if profile is empty). Previously we wrote
  // user.email here, so the customer-facing ticket said "tech@example.com"
  // even after the tech filled in their name.
  const myName = displayName(profile, user?.email);

  // Active roster for the employee picker on labor rows. Falls back
  // gracefully when orgUsers isn't loaded (techs can't read users — for
  // tech accounts the picker just won't have a list, but the "Other"
  // typed mode still works).
  const rosterUsers = (orgUsers || []).filter((u) => !u.deleted);
  const rosterNames = new Set(
    rosterUsers.map((u) => displayName(u.profile, u.email))
  );
  // Row IDs the user has explicitly switched to typed mode (picked
  // "Other" from the dropdown). Combined with auto-detection on existing
  // non-roster names, lets the user toggle between roster picker and
  // free-text without an explicit mode field on the row data.
  const [explicitTypedIds, setExplicitTypedIds] = useState(new Set());
  const isTypedRow = (id, name) =>
    explicitTypedIds.has(id) ||
    (name !== "" && !rosterNames.has(name));
  const markTyped = (id) =>
    setExplicitTypedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const unmarkTyped = (id) =>
    setExplicitTypedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const [showForm, setShowForm] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showSigned, setShowSigned] = useState(false);
  // null = creating a new ticket; ticket id = editing that submitted (not
  // yet signed) ticket. Audit trail entry appended on save.
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [summary, setSummary] = useState("");
  const [labor, setLabor] = useState([
    { id: newId(), employeeName: myName, hours: "", date: today(), payType: "straight" },
  ]);
  const [materials, setMaterials] = useState([]);
  const [equipmentIds, setEquipmentIds] = useState([]);

  const addLabor = () =>
    setLabor((p) => [
      ...p,
      {
        id: newId(),
        employeeName: "",
        hours: "",
        date: today(),
        payType: "straight",
      },
    ]);
  const editLabor = (id, field, value) =>
    setLabor((p) => p.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  const removeLabor = (id) => setLabor((p) => p.filter((l) => l.id !== id));

  const addMaterial = () =>
    setMaterials((p) => [
      ...p,
      {
        id: newId(),
        name: "",
        partNumber: "",
        qty: "",
        unitCost: "",
        source: "stock",
        poId: "",
        poItemId: "",
      },
    ]);

  // When tech picks a specific PO line item, copy that item's name/
  // partNumber/unitCost onto the material so the office copy can show
  // the captured price without a runtime PO lookup.
  const pickPOItem = (materialId, poId, poItemId) => {
    const po = jobPOs.find((p) => p.id === poId);
    const item = po && (po.items || []).find((it) => it.id === poItemId);
    if (!item) {
      // Clear selection
      setMaterials((p) =>
        p.map((m) =>
          m.id === materialId ? { ...m, poItemId: "" } : m
        )
      );
      return;
    }
    setMaterials((p) =>
      p.map((m) =>
        m.id === materialId
          ? {
              ...m,
              poItemId,
              name: item.name || m.name,
              partNumber: item.partNumber || m.partNumber,
              unitCost: item.unitCost || "",
              // qty stays editable — tech might use only part of the order
              qty: m.qty || item.qty || "",
            }
          : m
      )
    );
  };
  const editMaterial = (id, field, value) =>
    setMaterials((p) =>
      p.map((m) => (m.id === id ? { ...m, [field]: value } : m))
    );
  const removeMaterial = (id) =>
    setMaterials((p) => p.filter((m) => m.id !== id));

  const toggleEquipment = (eqId) =>
    setEquipmentIds((p) =>
      p.includes(eqId) ? p.filter((x) => x !== eqId) : [...p, eqId]
    );

  const resetForm = () => {
    setSummary("");
    setLabor([
      { id: newId(), employeeName: myName, hours: "", date: today(), payType: "straight" },
    ]);
    setMaterials([]);
    setEquipmentIds([]);
    setEditingTicketId(null);
  };

  // Load a submitted (not signed) ticket into the form for editing. The same
  // form fields drive both create and edit — the save handler branches on
  // editingTicketId. Equipment ids fall through unchanged if undefined.
  const startEditingTicket = (t) => {
    setSummary(t.summary || "");
    setLabor(
      (t.labor || []).length
        ? t.labor
        : [{ id: newId(), employeeName: myName, hours: "", date: today(), payType: "straight" }]
    );
    setMaterials(t.materials || []);
    setEquipmentIds(t.equipmentIds || []);
    setEditingTicketId(t.id);
    setShowForm(true);
    setExpandedId(null);
  };

  const handleCreate = async () => {
    const hasWork =
      summary.trim() || labor.some((l) => num(l.hours) > 0) || materials.length;
    if (!hasWork) return;
    // ticket.workDate is denormalized from the first employee's date so
    // the dashboard / sort can use a single value. If labor's empty (no
    // employees, just materials), fall back to today.
    const ticket = {
      ...createServiceTicket(),
      jobId: job.id,
      customerId: job.customerId,
      locationId: job.locationId,
      equipmentIds,
      workDate: labor[0]?.date || today(),
      summary,
      labor,
      materials,
      status: "submitted",
      submittedAt: new Date().toISOString(),
      createdBy: user?.email || "unknown",
      createdByUid: user?.uid || "",
    };
    setSubmitAttempted(true);
    await actions.addServiceTicket(ticket);
    // Don't reset/close here: a rejected write would otherwise wipe the typed
    // data. The effect below clears the form once the new ticket actually
    // lands in the list (confirmed success).
  };

  const handleSaveEdit = async () => {
    if (!editingTicketId) return;
    const t = tickets.find((x) => x.id === editingTicketId);
    if (!t || t.status === "signed") return; // shouldn't happen, but safety
    const now = new Date().toISOString();
    setSavingEdit(true);
    try {
      await actions.updateServiceTicket(editingTicketId, {
        workDate: labor[0]?.date || today(),
        summary,
        labor,
        materials,
        equipmentIds,
        updatedAt: now,
        auditTrail: [
          ...(t.auditTrail || []),
          {
            ts: now,
            userEmail: user?.email || "unknown",
            userName: myName,
            summary: "Edited after submission (before sign-off)",
          },
        ],
      });
      resetForm();
      setShowForm(false);
    } finally {
      setSavingEdit(false);
    }
  };

  // When a submit succeeds, the realtime listener delivers the new ticket and
  // tickets.length grows — that's our success signal. Reset + close then.
  const prevCount = useRef(tickets.length);
  useEffect(() => {
    if (submitAttempted && tickets.length > prevCount.current) {
      resetForm();
      setShowForm(false);
      setSubmitAttempted(false);
    }
    prevCount.current = tickets.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets.length, submitAttempted]);

  // Active (submitted) tickets sort by workDate desc — when work was done
  // is the most meaningful date for in-flight work. Signed tickets sort by
  // signedAt desc — for the archive view the most-recent SIGNING is what
  // people want to find first (multiple tickets can share a workDate, so
  // workDate alone leaves within-day order ambiguous).
  const activeTickets = [...tickets]
    .filter((t) => t.status !== "signed")
    .sort(
      (a, b) => new Date(b.workDate || 0) - new Date(a.workDate || 0)
    );
  const signedTickets = [...tickets]
    .filter((t) => t.status === "signed")
    .sort((a, b) => {
      const ak = a.signedAt || a.workDate || "";
      const bk = b.signedAt || b.workDate || "";
      return bk.localeCompare(ak);
    });

  const smallInput = { ...styles.input, width: "90px", margin: "2px" };

  const renderTicketRow = (t) => {
    const hours = (t.labor || []).reduce((s, l) => s + num(l.hours), 0);
    const open = expandedId === t.id;
    const eqNames = (t.equipmentIds || [])
      .map((id) => {
        const eq = equipmentList.find((e) => e.id === id);
        return eq ? `${eq.type}${eq.subType ? ` (${eq.subType})` : ""}` : null;
      })
      .filter(Boolean);
    return (
      <div
        key={t.id}
        style={{ borderTop: "1px solid #eee", padding: "6px 0", fontSize: "13px" }}
      >
        <div
          style={{ cursor: "pointer" }}
          onClick={() => setExpandedId(open ? null : t.id)}
        >
          {open ? "▾" : "▸"} <strong>{t.workDate}</strong>
          {" — "}
          {t.status === "signed" ? "Signed (locked)" : "Submitted"}
          {(t.auditTrail || []).length > 0 && (
            <span
              style={{
                fontSize: "11px",
                color: "#b16500",
                marginLeft: "6px",
                fontWeight: "bold",
              }}
              title={(t.auditTrail || [])
                .map(
                  (a) =>
                    `${a.userName || a.userEmail || "?"} on ${new Date(
                      a.ts
                    ).toLocaleString()}`
                )
                .join("\n")}
            >
              • edited ({(t.auditTrail || []).length}×)
            </span>
          )}
          <br />
          <span style={{ fontSize: "12px", color: "#666", marginLeft: "14px" }}>
            {hours} hr • {(t.labor || []).length} employee(s) •{" "}
            {(t.materials || []).length} material line(s)
          </span>
        </div>

        {open && (
          <div style={{ marginLeft: "14px", marginTop: "6px" }}>
            <div style={{ marginBottom: "4px" }}>
              <strong>Work done:</strong> {t.summary || "(no summary)"}
            </div>

            {eqNames.length > 0 && (
              <div style={{ marginBottom: "4px" }}>
                <strong>Equipment:</strong> {eqNames.join(", ")}
              </div>
            )}

            <div style={{ marginBottom: "4px" }}>
              <strong>Labor:</strong>
              {(t.labor || []).length === 0 ? (
                <span style={{ color: "#666" }}> none</span>
              ) : (
                <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                  {t.labor.map((l) => {
                    const ptLabel =
                      l.payType === "overtime"
                        ? "OT"
                        : l.payType === "double"
                        ? "DT"
                        : "";
                    return (
                      <li key={l.id}>
                        {l.employeeName || "—"} — {num(l.hours)} hr
                        {ptLabel && (
                          <span
                            style={{
                              fontSize: "10px",
                              color: "#b16500",
                              marginLeft: "4px",
                              fontWeight: "bold",
                            }}
                          >
                            {ptLabel}
                          </span>
                        )}
                        {l.date ? ` (${l.date})` : ""}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div>
              <strong>Materials:</strong>
              {(t.materials || []).length === 0 ? (
                <span style={{ color: "#666" }}> none</span>
              ) : (
                <ul style={{ margin: "2px 0", paddingLeft: "18px" }}>
                  {t.materials.map((m) => {
                    const po =
                      m.source === "po"
                        ? jobPOs.find((p) => p.id === m.poId)
                        : null;
                    return (
                      <li key={m.id}>
                        {m.name || "—"}
                        {m.partNumber ? ` (#${m.partNumber})` : ""} — qty{" "}
                        {num(m.qty)}
                        {m.source === "po" ? (
                          <span style={{ color: "#1a7f37" }}>
                            {" "}— from PO {po?.poNumber || "(linked)"}{" "}
                            {po?.vendor ? `(${po.vendor})` : ""}
                          </span>
                        ) : (
                          <>
                            {" "}@ ${num(m.unitCost)}
                            {m.source === "credit_card" && (
                              <span style={{ color: "#666" }}> — credit card</span>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Action row — Edit (if allowed) + Review&Sign + Delete + Move.
                Each child renders as a fragment, so the flex container lays
                everything on one row (wraps to multi-line on narrow screens).
                For SIGNED tickets, TicketSignOff renders its own self-
                contained section instead; this row is only for the editable
                lifecycle (submitted → ready-to-sign). */}
            {t.status === "signed" ? (
              <TicketSignOff
                ticket={t}
                job={job}
                equipmentList={equipmentList}
                actions={actions}
                canSign={canFile}
                customer={customer}
                location={location}
                customerName={customerName}
                locationName={locationName}
                logoUrl={logoUrl}
                orgName={orgName}
                orgAddress={orgAddress}
                /* Itemized copy intentionally NOT shown here: a tech
                   exporting from a customer-facing job view shouldn't
                   have a one-tap path that includes prices. Office staff
                   export the itemized copy from the standalone Service
                   Tickets dashboard page instead. */
                canViewCosts={false}
              />
            ) : (
              <div
                style={{
                  marginTop: "8px",
                  paddingTop: "6px",
                  borderTop: "1px dashed #ccc",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {canFile && (
                  <button
                    style={styles.button}
                    onClick={() => startEditingTicket(t)}
                  >
                    Edit
                  </button>
                )}
                <TicketSignOff
                  ticket={t}
                  job={job}
                  equipmentList={equipmentList}
                  actions={actions}
                  canSign={canFile}
                  customer={customer}
                  location={location}
                  customerName={customerName}
                  locationName={locationName}
                  logoUrl={logoUrl}
                  orgName={orgName}
                  orgAddress={orgAddress}
                  /* Itemized copy lives on the Service Tickets
                     dashboard page only — see the signed branch above. */
                  canViewCosts={false}
                />
                <TicketActions
                  ticket={t}
                  jobs={jobsRaw}
                  actions={actions}
                  canManage={canManage}
                  role={role}
                  capabilities={capabilities}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.card}>
      <strong>Service Tickets ({activeTickets.length})</strong>

      {activeTickets.length === 0 ? (
        <p style={{ fontSize: "12px", color: "#666" }}>
          No open tickets.
        </p>
      ) : (
        activeTickets.map(renderTicketRow)
      )}

      {signedTickets.length > 0 && (
        <div style={{ marginTop: "10px" }}>
          <div
            style={{ cursor: "pointer", fontSize: "13px", color: "#1a7f37" }}
            onClick={() => setShowSigned((s) => !s)}
          >
            {showSigned ? "▾" : "▸"} <strong>Signed &amp; locked (
            {signedTickets.length})</strong>
          </div>
          {showSigned && signedTickets.map(renderTicketRow)}
        </div>
      )}

      {!canFile && (
        <div
          style={{
            marginTop: "10px",
            padding: "8px 10px",
            background: "#f3f4f6",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "12px",
            color: "#374151",
          }}
        >
          <strong>Tickets locked.</strong>{" "}
          {job?.billFinalized
            ? "Bill on this job is finalized — reopen the bill (from JobCosting) to add or edit tickets."
            : job?.status === "closed"
            ? "Job is closed — reopen the job to add or edit tickets."
            : job?.status === "in_progress"
            ? null
            : "Job hasn't been started yet — assign a tech and click Start Job to unlock tickets."}
        </div>
      )}

      {canFile && (
        <div style={{ marginTop: "8px" }}>
          <button
            style={styles.button}
            onClick={() => {
              // Toggle off-and-reset to escape edit mode cleanly. Toggling
              // on returns to "create new" defaults.
              if (showForm) {
                resetForm();
                setShowForm(false);
                setSubmitAttempted(false);
              } else {
                setShowForm(true);
              }
            }}
          >
            {showForm
              ? editingTicketId
                ? "Cancel edit"
                : "Hide New Ticket"
              : "New Ticket"}
          </button>

          {showForm && (
            <div
              style={{
                marginTop: "8px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                padding: "8px",
              }}
            >
              {editingTicketId && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#b16500",
                    marginBottom: "6px",
                    fontWeight: "bold",
                  }}
                >
                  Editing submitted ticket — an audit-trail entry is added on
                  save.
                </div>
              )}
              <textarea
                style={styles.textarea}
                placeholder="What was done? (summary of the work performed)"
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
              />

              {/* Equipment worked on */}
              {equipmentList.length > 0 && (
                <div style={{ marginTop: "6px" }}>
                  <strong style={{ fontSize: "12px" }}>
                    Equipment worked on:
                  </strong>
                  {equipmentList.map((eq) => (
                    <label
                      key={eq.id}
                      style={{ display: "block", fontSize: "12px" }}
                    >
                      <input
                        type="checkbox"
                        checked={equipmentIds.includes(eq.id)}
                        onChange={() => toggleEquipment(eq.id)}
                      />{" "}
                      {eq.type}
                      {eq.subType ? ` (${eq.subType})` : ""}
                    </label>
                  ))}
                </div>
              )}

              {/* Labor — multiple employees */}
              <div style={{ marginTop: "10px" }}>
                <strong style={{ fontSize: "12px" }}>
                  Employees &amp; hours:
                </strong>
                {labor.map((l) => {
                  const typed = isTypedRow(l.id, l.employeeName);
                  return (
                  <div key={l.id} style={{ fontSize: "12px", marginTop: "2px" }}>
                    {typed ? (
                      <>
                        <input
                          style={{ ...smallInput, width: "150px" }}
                          placeholder="Type employee name"
                          value={l.employeeName}
                          onChange={(e) =>
                            editLabor(l.id, "employeeName", e.target.value)
                          }
                        />
                        <button
                          type="button"
                          style={{
                            ...styles.button,
                            padding: "2px 6px",
                            fontSize: "10px",
                          }}
                          onClick={() => {
                            unmarkTyped(l.id);
                            editLabor(l.id, "employeeName", "");
                          }}
                          title="Pick from roster instead"
                        >
                          roster
                        </button>
                      </>
                    ) : (
                      <select
                        style={{ ...smallInput, width: "160px" }}
                        value={l.employeeName || ""}
                        onChange={(e) => {
                          if (e.target.value === "__OTHER__") {
                            markTyped(l.id);
                            editLabor(l.id, "employeeName", "");
                          } else {
                            editLabor(l.id, "employeeName", e.target.value);
                          }
                        }}
                      >
                        <option value="">— pick employee —</option>
                        {rosterUsers.map((u) => {
                          const name = displayName(u.profile, u.email);
                          return (
                            <option key={u.id} value={name}>
                              {name}
                            </option>
                          );
                        })}
                        <option value="__OTHER__">Other (type in)</option>
                      </select>
                    )}
                    <input
                      style={smallInput}
                      type="number"
                      placeholder="Hours"
                      value={l.hours}
                      onChange={(e) => editLabor(l.id, "hours", e.target.value)}
                    />
                    <input
                      style={{ ...smallInput, width: "130px" }}
                      type="date"
                      value={l.date}
                      onChange={(e) => editLabor(l.id, "date", e.target.value)}
                    />{" "}
                    <select
                      style={{ ...smallInput, width: "70px" }}
                      value={l.payType || "straight"}
                      onChange={(e) =>
                        editLabor(l.id, "payType", e.target.value)
                      }
                      title="Pay type — carries into payroll on sign-off"
                    >
                      <option value="straight">ST</option>
                      <option value="overtime">OT</option>
                      <option value="double">DT</option>
                    </select>{" "}
                    <button
                      style={{ ...styles.button, color: "red", padding: "2px 6px" }}
                      onClick={() => removeLabor(l.id)}
                    >
                      x
                    </button>
                  </div>
                  );
                })}
                <button style={styles.button} onClick={addLabor}>
                  + Employee
                </button>
              </div>

              {/* Materials — tech-entered cost (hidden from customer later) */}
              <div style={{ marginTop: "10px" }}>
                <strong style={{ fontSize: "12px" }}>
                  Materials used (cost is internal — not shown to customer):
                </strong>
                {materials.map((m) => {
                  const isPO = m.source === "po";
                  return (
                    <div
                      key={m.id}
                      style={{
                        fontSize: "12px",
                        marginTop: "6px",
                        paddingTop: "4px",
                        borderTop: "1px dashed #eee",
                      }}
                    >
                      {/* Source row first — picking PO auto-fills the
                          name / part / cost from the chosen PO item below,
                          so the tech doesn't waste typing into those. */}
                      <div>
                        <label style={{ fontSize: "11px", color: "#666" }}>
                          Source:{" "}
                          <select
                            value={m.source || "stock"}
                            onChange={(e) => {
                              editMaterial(m.id, "source", e.target.value);
                              // Clear PO id when leaving PO source
                              if (e.target.value !== "po") {
                                editMaterial(m.id, "poId", "");
                              }
                            }}
                            style={{ fontSize: "11px" }}
                          >
                            <option value="stock">Truck stock</option>
                            <option value="credit_card">Credit card</option>
                            <option value="po">Purchase order</option>
                          </select>
                        </label>
                        {isPO && (
                          <label style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}>
                            PO:{" "}
                            <select
                              value={m.poId || ""}
                              onChange={(e) => {
                                editMaterial(m.id, "poId", e.target.value);
                                // Switching POs invalidates the item pick.
                                editMaterial(m.id, "poItemId", "");
                              }}
                              style={{ fontSize: "11px" }}
                            >
                              <option value="">— pick PO —</option>
                              {jobPOs.map((po) => (
                                <option key={po.id} value={po.id}>
                                  {po.poNumber || "(no #)"}{" "}
                                  {po.vendor ? `— ${po.vendor}` : ""}{" "}
                                  ({po.status})
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {isPO && m.poId && (() => {
                          const po = jobPOs.find((p) => p.id === m.poId);
                          const items = (po && po.items) || [];
                          return (
                            <label style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}>
                              Item:{" "}
                              <select
                                value={m.poItemId || ""}
                                onChange={(e) =>
                                  pickPOItem(m.id, m.poId, e.target.value)
                                }
                                style={{ fontSize: "11px" }}
                              >
                                <option value="">— pick item —</option>
                                {items.map((it) => (
                                  <option key={it.id} value={it.id}>
                                    {it.name || "(unnamed)"}
                                    {it.partNumber ? ` (#${it.partNumber})` : ""}
                                    {` — $${num(it.unitCost).toFixed(2)}`}
                                    {it.qty ? ` × ${num(it.qty)}` : ""}
                                  </option>
                                ))}
                              </select>
                              {items.length === 0 && (
                                <span style={{ color: "#a55", marginLeft: "4px" }}>
                                  (this PO has no items yet)
                                </span>
                              )}
                            </label>
                          );
                        })()}
                        {isPO && (
                          <span style={{ fontSize: "11px", color: "#1a7f37", marginLeft: "6px", display: "block", marginTop: "2px" }}>
                            (cost from PO — won't double-count on bill)
                          </span>
                        )}
                      </div>
                      {/* Material details. For PO source the name/part/unit
                          cost auto-populate from the picked PO item; tech
                          mostly just edits qty. */}
                      <div style={{ marginTop: "4px" }}>
                        <input
                          style={{ ...smallInput, width: "130px" }}
                          placeholder="Material name"
                          value={m.name}
                          onChange={(e) =>
                            editMaterial(m.id, "name", e.target.value)
                          }
                        />
                        <input
                          style={smallInput}
                          placeholder="Part #"
                          value={m.partNumber}
                          onChange={(e) =>
                            editMaterial(m.id, "partNumber", e.target.value)
                          }
                        />
                        <input
                          style={smallInput}
                          type="number"
                          placeholder="Qty"
                          value={m.qty}
                          onChange={(e) =>
                            editMaterial(m.id, "qty", e.target.value)
                          }
                        />
                        {!isPO && (
                          <input
                            style={smallInput}
                            type="number"
                            placeholder="Unit cost $"
                            value={m.unitCost}
                            onChange={(e) =>
                              editMaterial(m.id, "unitCost", e.target.value)
                            }
                          />
                        )}{" "}
                        <button
                          style={{
                            ...styles.button,
                            color: "red",
                            padding: "2px 6px",
                          }}
                          onClick={() => removeMaterial(m.id)}
                        >
                          x
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button style={{ ...styles.button, marginTop: "6px" }} onClick={addMaterial}>
                  + Material
                </button>
              </div>

              <div style={{ marginTop: "10px" }}>
                {editingTicketId ? (
                  <button
                    style={styles.button}
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? "Saving..." : "Save Edits"}
                  </button>
                ) : (
                  <button style={styles.button} onClick={handleCreate}>
                    Submit Ticket
                  </button>
                )}
                <button
                  style={styles.button}
                  onClick={() => {
                    resetForm();
                    setSubmitAttempted(false);
                    setShowForm(false);
                  }}
                >
                  {editingTicketId ? "Cancel" : "Close"}
                </button>
                {submitAttempted && errorMessage && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "6px" }}>
                    Couldn't save ticket: {errorMessage}
                  </div>
                )}
                {submitAttempted && !errorMessage && !editingTicketId && (
                  <div
                    style={{ color: "green", fontSize: "12px", marginTop: "6px" }}
                  >
                    Saved — see the list above. You can Close this form.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
